import React from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

// Domain color schemes for consistent styling
const domainColors = {
  'knowledgia': { bg: '#e3f2fd', color: '#1565c0' },
  'thecozyguide': { bg: '#e8f5e9', color: '#2e7d32' },
  'default': { bg: '#f5f5f5', color: '#616161' }
};

const getDomainColor = (domain) => {
  const key = domain?.toLowerCase();
  return domainColors[key] || domainColors['default'];
};

/**
 * Component for displaying summary metrics cards
 */
const SummaryMetrics = ({ summaryMetrics }) => {
  const {
    totalCampaigns,
    totalRevenue,
    totalCost,
    totalProfit,
    averageRoas,
    averagePayout,
    averageCpc,
    totalClicks = 0,
    totalConversions,
    avgCtr,
    totalActiveBudget = 0,
    domainMetrics = {}
  } = summaryMetrics;
  
  return (
    <>
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Campaigns</Typography>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{totalCampaigns}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Revenue</Typography>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>${totalRevenue.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Cost</Typography>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>${totalCost.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Profit</Typography>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }} color={totalProfit >= 0 ? 'success.main' : 'error.main'}>
                ${totalProfit.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">ROAS</Typography>
              <Tooltip title="Return on Ad Spend: Revenue / Cost. Value of 1.0 = breakeven, >1.0 is profitable">
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }} color={averageRoas >= 1 ? 'success.main' : 'error.main'}>
                  {averageRoas.toFixed(2)}
                </Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">EPC</Typography>
              <Tooltip title="Earnings Per Click: Revenue per click">
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>${averagePayout.toFixed(2)}</Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">CPC</Typography>
              <Tooltip title="Cost Per Click: Total cost divided by total Facebook ad clicks">
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>${averageCpc.toFixed(2)}</Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Bought Clicks</Typography>
              <Tooltip title="Total Facebook ad clicks purchased">
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{(totalClicks || 0).toLocaleString()}</Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Conversions</Typography>
              <Tooltip title="Total Conversions: Total demand provider clicks that generate revenue">
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{(totalConversions || 0).toLocaleString()}</Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">CTR</Typography>
              <Tooltip title="Click-Through Rate: Facebook ad clicks / Facebook impressions × 100">
                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{(avgCtr || 0).toFixed(2)}%</Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ boxShadow: 1, borderRadius: 1, height: '100%', backgroundColor: '#e3f2fd' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Active Budget</Typography>
              <Tooltip title="Total daily budget of all active campaigns">
                <Typography variant="h6" sx={{ fontSize: '1.1rem', color: '#1565c0' }}>
                  ${totalActiveBudget.toFixed(2)}
                </Typography>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Domain Breakdown Table */}
      {Object.keys(domainMetrics).length > 0 && (
        <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
            Domain Breakdown
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Domain</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Campaigns</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Active Budget</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>ROAS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>EPC</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>CVR</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(domainMetrics)
                  .sort((a, b) => b[1].revenue - a[1].revenue)
                  .map(([domain, metrics]) => {
                    const colorScheme = getDomainColor(domain);
                    return (
                      <TableRow key={domain} sx={{ backgroundColor: colorScheme.bg }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, color: colorScheme.color, fontSize: '0.8rem' }}>
                            {domain}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          {metrics.campaigns}
                          {metrics.activeCampaigns > 0 && (
                            <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.7rem', ml: 0.5 }}>
                              ({metrics.activeCampaigns} active)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          ${metrics.budget.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: metrics.roas >= 1 ? 'success.main' : 'error.main'
                            }}
                          >
                            {metrics.roas.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          ${metrics.epc.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                          {metrics.cvr.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
};

export default SummaryMetrics; 